// SysIgreja monorepo - frontend
import { useSincronizarOnboardingPreview } from "@/hooks/useOnboardingPreview";
import { lazy, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "./Routes";
import globalRouter from "./lib/globalRouter";
import { useAppDispatch } from "./redux/hooks";
import { resetState, syncAuthenticatedUserFromPeer, clearAuthStateOnly, setAuthenticatedUser } from "./redux/slices/auth.slice";
import { PageViewLogger } from "@/components/page-view-logger";
import { StoreGate } from "@/components/store-gate";
import { CredencialFontsGate } from "@/components/font-loader/CredencialFontsGate";
// #728: banner de cookies fora da entrada (baixa sob demanda).
const CookieConsent = lazy(() => import("@/components/cookie-consent/CookieConsent"));
import {
  ensureValidSession,
  isEnsureSessionFailure,
  recoverSessionAfterPeerRefresh,
} from "@/lib/auth-session";
import { postAuthSessionLogOnce } from "@/lib/auth-session-log";
import { IUsuario } from "@/interfaces/usuarios.interface";
import { SessionProvider } from "@/contexts/SessionContext";
import { FeatureFlagOverridesProvider } from "@/contexts/FeatureFlagOverridesContext";
import { postLog } from "@/lib/logs";
import { isPublicAuthPath } from "@/lib/auth-public-paths";
import {
  clearChunkReloadGuard,
  isChunkLoadError,
  recoverFromChunkLoadError,
} from "@/lib/lazy-with-retry";
import {
  isErrorLogNoise,
  isErrorLogNoiseReason,
  serializeUnknownError,
} from "@/lib/error-log-noise";
import {
  buildErrorLogDedupeKey,
  shouldSkipDuplicateErrorLog,
} from "@/lib/error-log-dedupe";
import { buildErrorEnvelope } from "@/lib/error-envelope";
import { getClientContext } from "@/lib/client-context";
import { useFeatureFlagsSync } from "@/hooks/useFeatureFlagsSync";
import { useUnleashUserContext } from "@/hooks/useUnleashUserContext";
import { useOrigemAquisicaoSync } from "@/hooks/useOrigemAquisicaoSync";
import { store } from "@/redux/store";
import { notificacoesAPI } from "@/services/notificacoes.service";
import { setLoginId } from "@/redux/slices/general.slice";
import { resolveNotificationNavigation } from "@/utils/notification-navigation";

async function applySessionFailure(
  dispatch: ReturnType<typeof useAppDispatch>,
  navigate: ReturnType<typeof useNavigate>,
  reason: string,
  contexto: string,
) {
  if (reason !== "expired") return;

  const recovered = await recoverSessionAfterPeerRefresh();
  if (recovered) {
    dispatch(setAuthenticatedUser(recovered as IUsuario));
    return;
  }

  void postAuthSessionLogOnce({
    tipo: "Auth_Falhou_Renovar_Sessao",
    acao: "sessao_expirada",
    reason,
    contexto,
  });

  dispatch(resetState());

  const pathname = window.location.pathname;
  if (!isPublicAuthPath(pathname) && pathname !== "/login") {
    localStorage.setItem("redirectAfterLogin", pathname + window.location.search);
    navigate("/login", { replace: true });
  }
}

export function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  globalRouter.navigate = navigate;
  useFeatureFlagsSync();
  useUnleashUserContext();
  useSincronizarOnboardingPreview();
  useOrigemAquisicaoSync();

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "user") return;
      if (event.newValue) {
        try {
          dispatch(syncAuthenticatedUserFromPeer(JSON.parse(event.newValue) as IUsuario));
        } catch {
          /* ignore */
        }
        return;
      }
      if (event.oldValue && !event.newValue) {
        dispatch(clearAuthStateOnly());
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);
  useEffect(() => {
    void (async () => {
      const result = await ensureValidSession({ background: false, maxAttempts: 1 });
      if (result.ok) {
        if (result.user) {
          dispatch(setAuthenticatedUser(result.user as IUsuario));
        }
        if (result.refreshed) {
          void postLog({
            Tipo: "Auth_Renovou_Sessao",
            Objeto: { acao: "bootstrap", contexto: "app_init" },
          });
        }
        return;
      }

      if (isEnsureSessionFailure(result)) {
        await applySessionFailure(dispatch, navigate, result.reason, "app_init");
      }
    })();
  }, [dispatch, navigate]);

  useEffect(() => {
    const restoreSession = async (contexto: string) => {
      const result = await ensureValidSession({ background: true });
      if (result.ok) {
        if (result.user) {
          dispatch(setAuthenticatedUser(result.user as IUsuario));
        }
        if (result.refreshed) {
          void postLog({
            Tipo: "Auth_Renovou_Sessao",
            Objeto: { acao: "proativo", contexto },
          });
        }
        return;
      }

      if (isEnsureSessionFailure(result)) {
        await applySessionFailure(dispatch, navigate, result.reason, contexto);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void restoreSession("visibilitychange");
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void restoreSession("pageshow_bfcache");
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    const onSwMessage = async (event: MessageEvent) => {
      if (event.data?.type !== "NOTIFICATION_NAVIGATE") return;

      const targetUrl = typeof event.data.url === "string" ? event.data.url : "/sistema";
      const notificacaoId =
        typeof event.data.notificacaoId === "number"
          ? event.data.notificacaoId
          : typeof event.data.notificacaoId === "string"
            ? parseInt(event.data.notificacaoId, 10)
            : undefined;
      const pushLoginId =
        typeof event.data.loginId === "number"
          ? event.data.loginId
          : typeof event.data.loginId === "string"
            ? parseInt(event.data.loginId, 10)
            : undefined;

      void postLog({
        Tipo: "Notificacoes_Clicou_Deeplink",
        Objeto: { url: targetUrl, notificacaoId, loginId: pushLoginId },
      });

      if (pushLoginId && !Number.isNaN(pushLoginId)) {
        dispatch(setLoginId(pushLoginId));
      }

      if (notificacaoId && !Number.isNaN(notificacaoId) && pushLoginId && !Number.isNaN(pushLoginId)) {
        try {
          await store.dispatch(
            notificacoesAPI.endpoints.markNotificationAsRead.initiate({
              id: notificacaoId,
              loginId: pushLoginId,
            }),
          ).unwrap();
        } catch {
          /* ignora falha de marcação */
        }
      }

      const result = await ensureValidSession({ background: false, maxAttempts: 2 });
      if (result.ok && result.user) {
        dispatch(setAuthenticatedUser(result.user as IUsuario));
      } else if (isEnsureSessionFailure(result)) {
        await applySessionFailure(dispatch, navigate, result.reason, "notification_deeplink");
      }

      const dest = resolveNotificationNavigation(targetUrl);
      if (dest.mode === "assign") {
        window.location.assign(dest.url);
      } else {
        navigate(dest.path);
      }

      try {
        const countResult = await store.dispatch(
          notificacoesAPI.endpoints.getUnreadNotificationsCount.initiate(undefined, {
            forceRefetch: true,
          }),
        ).unwrap();
        const count = countResult?.count ?? 0;
        if ("setAppBadge" in navigator) {
          if (count > 0) {
            await navigator.setAppBadge(count);
          } else {
            await navigator.clearAppBadge?.();
          }
        }
      } catch {
        /* badge opcional */
      }
    };

    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onSwMessage);
  }, [dispatch, navigate]);

  useEffect(() => {
    // Só libera o one-shot após a página estabilizar (evita loop se o chunk ainda falhar).
    const t = window.setTimeout(() => clearChunkReloadGuard(), 4000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const logErro = (
      error: {
        name?: string;
        message?: string;
        stack?: string;
        status?: number | string;
        apiMessage?: string;
      },
      origem: string,
    ) => {
      const route = window.location.pathname;
      const dedupeKey = buildErrorLogDedupeKey({
        message: error.message,
        name: error.name,
        route,
        status: error.status,
        origem,
      });
      if (shouldSkipDuplicateErrorLog(dedupeKey)) return;

      const status =
        typeof error.status === "number"
          ? error.status
          : typeof error.status === "string" && /^\d+$/.test(error.status)
            ? Number(error.status)
            : undefined;
      const client = getClientContext();
      const envelope = buildErrorEnvelope({
        tipo: "Erro",
        kind: origem === "unhandledrejection" || origem === "window.onerror" ? "unhandled" : "client",
        status,
        message: error.message ?? error.apiMessage ?? "",
        operation: origem,
        route,
        name: error.name ?? "Error",
        commitSha: client.client.commitSha,
      });

      void postLog({
        Tipo: "Erro",
        Objeto: {
          error: {
            ...envelope,
            Name: error.name,
            Message: error.message,
            stack: error.stack?.slice(0, 4000),
            ...(error.apiMessage ? { apiMessage: error.apiMessage } : {}),
          },
          origem,
        },
      }).catch(() => undefined);
    };

    const onError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error ?? event.message)) {
        event.preventDefault?.();
        recoverFromChunkLoadError({ origem: "window.onerror" });
        return;
      }
      if (isErrorLogNoise(event.message) || isErrorLogNoise(event.error?.message)) {
        return;
      }
      const payload = {
        name: event.error?.name,
        message: event.message,
        stack: event.error?.stack,
      };
      logErro(payload, "window.onerror");
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (isChunkLoadError(reason)) {
        event.preventDefault?.();
        recoverFromChunkLoadError({ origem: "unhandledrejection" });
        return;
      }
      if (isErrorLogNoiseReason(reason)) {
        event.preventDefault?.();
        return;
      }
      const payload = serializeUnknownError(reason);
      if (isErrorLogNoise(payload.message) || isErrorLogNoise(payload.name)) {
        event.preventDefault?.();
        return;
      }
      logErro(payload, "unhandledrejection");
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <div className="App">
      <SessionProvider status="ready">
        <FeatureFlagOverridesProvider>
          <StoreGate>
            <CredencialFontsGate />
            <PageViewLogger>
              <Routes />
              <Suspense fallback={null}>
                <CookieConsent />
              </Suspense>
            </PageViewLogger>
          </StoreGate>
        </FeatureFlagOverridesProvider>
      </SessionProvider>
    </div>
  );
}
